import { Injectable, inject } from '@angular/core';
import { HeatGeneratorEnergySourceInformation, ProjectResponseGetById } from '@nx-customer-apps/api-planning-projects';
import { EnergyConsumptionFormValue, EnergyConsumptionOptions } from '@nx-customer-apps/shared/interfaces';
import { Observable, filter, first, map, merge, withLatestFrom } from 'rxjs';
import { ProjectStore } from '../../state/project/project.store';
import { HeatPumpIntermediaryStore } from '../../state/heat-pump-intermediary/heat-pump-intermediary.store';
import { HeatloadFormOptionsService } from '../../heatload/services';
import { FormName, ProjectType } from '@nx-customer-apps/shared/enums';
import { TranslateService } from '@ngx-translate/core';
import { HeatPumpIntermediaryPrefillService } from './heat-pump-intermediary-prefill.service';
import { Question } from '@nx-customer-apps/shared/utils';
import { HeatloadPrefillMapper, ProjectsService } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { EnergyConsumptionFormService } from '../../shared/components/consumption';
import { BuildingType, HeatLoadCalculationMethod } from '@nx-customer-apps/components';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediaryEnergyConsumptionService implements AccordionService {
    private projectStore = inject(ProjectStore);
    private projectsService = inject(ProjectsService);
    private heatPumpIntermediaryStore = inject(HeatPumpIntermediaryStore);
    private formService = inject(EnergyConsumptionFormService);
    private formOptionsService = inject(HeatloadFormOptionsService);
    private translate = inject(TranslateService);
    private prefillMapper = inject(HeatloadPrefillMapper);
    private prefillService = inject(HeatPumpIntermediaryPrefillService);

    public questions$: Observable<Question[] | null> = merge(
        this.qustionsForRenovationProject$(),
        this.qustionsForNewConstructionProject$(),
        this.qustionsForNewConstructionProjectCalculatedByLivingArea$()
    );

    public getSuccessMessage(
        formValue: EnergyConsumptionFormValue,
        selectedEnergySource: HeatGeneratorEnergySourceInformation | undefined
    ): string {
        const nonHeatingEnergyUnit = this.translate.instant('MEASUREMENT_UNITS.KWH');
        const options = {
            nonHeatingEnergy: formValue.nonHeatingElectricityPerYear,
            nonHeatingEnergyUnit
        };

        if (!selectedEnergySource) {
            return this.translate.instant('HEATLOAD.ENERGY_CONSUMPTION.NON_HEATING_ENERGY.SUCCESS_MESSAGE', options);
        }

        const keySuffix = selectedEnergySource.unit.toUpperCase();
        const requiredEnergyUnit = this.translate.instant(`MEASUREMENT_UNITS.${keySuffix}`);
        const requiredEnergyOptions = {
            ...options,
            requiredEnergy: formValue.requiredEnergyPerYear,
            requiredEnergyUnit
        };
        return this.translate.instant('HEATLOAD.ENERGY_CONSUMPTION.SUCCESS_MESSAGE', requiredEnergyOptions);
    }

    public isPrefillNeeded(): boolean {
        return !this.prefillService.isPrefilled(FormName.EnergyConsumption);
    }

    public afterPrefill(): void {
        this.prefillService.setAsPrefilled(FormName.EnergyConsumption);
    }

    private qustionsForRenovationProject$() {
        return this.heatPumpIntermediaryStore.heatGeneratorDetails$.pipe(
            withLatestFrom(this.projectStore.project$, this.heatPumpIntermediaryStore.cache$, (heatGeneratorDetails, project, cache) => ({
                heatGeneratorDetails,
                project,
                cache
            })),
            filter(({ project }) => this.projectsService.isRenovation(project!.projectType as ProjectType)),
            map(state => {
                if (!state.heatGeneratorDetails) {
                    return null;
                }

                const cachePrefill = this.prefillMapper.stateToEnergyConsumptionPrefill(state.cache?.energyConsumption);
                const projectPrefill = this.prefillMapper.projectToEnergyConsumptionPrefill(state.project);
                const options = this.formOptionsService.getEnergyConsumptionFormOptions({
                    buildingType: state.project!.building.buildingType as BuildingType,
                    prefill: cachePrefill || projectPrefill
                });

                return this.formService.getEnergyConsumptionQuestions(options as EnergyConsumptionOptions);
            })
        );
    }

    private qustionsForNewConstructionProject$() {
        return this.heatPumpIntermediaryStore.livingArea$.pipe(
            withLatestFrom(this.projectStore.project$, (livingArea, project) => ({ livingArea, project })),
            filter(
                ({ project }) =>
                    this.projectsService.isNewConstruction(project!.projectType as ProjectType) &&
                    !this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.LivingSpace)
            ),
            map(state => {
                if (!state.livingArea) {
                    return null;
                }

                return this.returnQuestionsForNewConstruction(state.project!);
            })
        );
    }
    private qustionsForNewConstructionProjectCalculatedByLivingArea$() {
        return this.projectStore.project$.pipe(
            filter(Boolean),
            first(),
            filter(
                project =>
                    this.projectsService.isNewConstruction(project!.projectType as ProjectType) &&
                    this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.LivingSpace)
            ),
            map(project => this.returnQuestionsForNewConstruction(project!))
        );
    }

    private returnQuestionsForNewConstruction(project: ProjectResponseGetById): Question[] {
        const projectPrefill = this.prefillMapper.projectToEnergyConsumptionPrefill(project);
        const options = this.formOptionsService.getEnergyConsumptionFormOptions({
            buildingType: project?.building.buildingType as BuildingType,
            prefill: projectPrefill
        });

        return this.formService.getEnergyConsumptionQuestions(options as EnergyConsumptionOptions);
    }

    public isExpanded$ = this.heatPumpIntermediaryStore.sameValueSubmited$.pipe(
        withLatestFrom(this.projectStore.project$, (form, project) => ({ form, project })),
        map(({ form, project }) => {
            const afterHeatGeneratorDetailsForm =
                form?.formName === FormName.HeatGeneratorDetails && this.projectsService.isRenovation(project!.projectType as ProjectType);
            const afterLivingAreaForm =
                form?.formName === FormName.LivingArea &&
                this.projectsService.isNewConstruction(project!.projectType as ProjectType) &&
                !this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.LivingSpace);
            return afterHeatGeneratorDetailsForm || afterLivingAreaForm;
        })
    );
}
